<script setup lang="ts">
import { computed } from 'vue'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  BOOTSTRAP_MENU_CLASSES,
  BOOTSTRAP_MENU_OPTION_CLASSES,
  BOOTSTRAP_MENU_SIDE_OFFSET
} from '@/components/dropdown-menu'
import LockedOption from './LockedOption.vue'
import type { FilterDropdownFilter } from './filter_dropdown'

const TRIGGER_SIZE_CLASSES = {
  default: 'text-text-md',
  lg: 'min-h-[46px] text-text-xl'
}

const MUTED_TRIGGER_TEXT_CLASSES = 'text-gray-700 hover:text-gray-900 focus-visible:text-gray-900 data-[state=open]:text-gray-900'

// Vue 2's strategy and goal wizard menus turn an option blue under the pointer, while the keyboard
// highlight stays Bootstrap's light gray. reka-ui focuses the option under the pointer, so hover is
// restated over focus.
const OPTION_CLASSES = `${BOOTSTRAP_MENU_OPTION_CLASSES} whitespace-nowrap hover:bg-blue-400 hover:text-white hover:focus:bg-blue-400 hover:focus:text-white`
const LOCKED_OPTION_CLASSES = 'data-[disabled]:cursor-not-allowed data-[disabled]:opacity-100'
// Vue 2's goal wizard lays out every option of a menu with locks on looser lines, past the hanging padlocks
const OPTIONS_WITH_LOCKS_CLASSES = 'mb-1 pl-7 leading-normal'

const NAVBAR_HEIGHT = 64
const MENU_COLLISION_PADDING = { top: NAVBAR_HEIGHT, bottom: 16 }
// No amount of menu below the viewport's foot counts as a collision, so the menu is never
// flipped above its trigger, while the sideways shift that keeps it on screen still applies.
const OPEN_BELOW_COLLISION_PADDING = { ...MENU_COLLISION_PADDING, bottom: -Infinity }
const VIEWPORT_HEIGHT_CAP = 'max-h-[var(--reka-popper-available-height)]'

const props = withDefaults(
  defineProps<{
    filter: FilterDropdownFilter
    triggerLabel: string
    clearable?: boolean
    size?: keyof typeof TRIGGER_SIZE_CLASSES
    mutedWhenEmpty?: boolean
    // A consumer raised above a backdrop must portal the menu into itself, or the menu
    // renders below that backdrop, dimmed and unclickable.
    containerPortal?: string
    // Opens the menu below its trigger at full height, extending the page rather than the
    // menu scrolling or flipping above, as the Vue 2 wizards' menus do.
    opensBelowAtFullHeight?: boolean
  }>(),
  { clearable: true, size: 'default', mutedWhenEmpty: false, containerPortal: undefined, opensBelowAtFullHeight: false }
)

const emit = defineEmits<{
  select: [value: string | string[] | null]
  open: []
}>()

const hasLockedOptions = computed(() => props.filter.options.some((option) => option.locked))

const menuCollisionPadding = computed(() =>
  props.opensBelowAtFullHeight ? OPEN_BELOW_COLLISION_PADDING : MENU_COLLISION_PADDING
)
const menuPositionStrategy = computed(() => (props.opensBelowAtFullHeight ? 'absolute' : 'fixed'))
const isHeightCapped = computed(() => !props.opensBelowAtFullHeight)

const triggerTextClass = computed(() =>
  props.mutedWhenEmpty && !props.filter.valueLabel ? MUTED_TRIGGER_TEXT_CLASSES : 'text-gray-900'
)

function selectOption(optionLabel: string) {
  const option = props.filter.options.find((candidate) => candidate.label === optionLabel)
  emit('select', option?.value ?? null)
}

function menuOpenChanged(open: boolean) {
  if (open) {
    emit('open')
  }
}
</script>

<template>
  <!-- non-modal: a modal menu marks the rest of the page aria-hidden while open, which axe
       reports as aria-hidden-focus -->
  <DropdownMenu :modal="false" @update:open="menuOpenChanged">
    <DropdownMenuTrigger
      :data-testid="`filter-${filter.field}`"
      :disabled="filter.disabled"
      :aria-label="triggerLabel"
      class="inline-flex items-center gap-1 rounded-xs border border-gray-400 bg-white px-3 py-1.5 align-middle leading-normal hover:border-gray-500 hover:bg-gray-300 focus-visible:border-gray-1000 focus-visible:bg-gray-300 disabled:opacity-65 data-[state=open]:border-gray-1000 data-[state=open]:bg-gray-300"
      :class="[TRIGGER_SIZE_CLASSES[size], triggerTextClass]"
    >
      {{ filter.valueLabel || filter.emptyText }}
      <span v-if="filter.options.length" class="material-icons-round text-[1em]" aria-hidden="true">
        arrow_drop_down
      </span>
    </DropdownMenuTrigger>

    <!-- unless it opens below at full height, capped to the space on the side it opens to:
         a fixed cap taller than that space flips the menu over the trigger, and a trigger
         low on the page then puts the menu over the navbar -->
    <DropdownMenuContent
      align="start"
      :container-portal="containerPortal"
      :collision-padding="menuCollisionPadding"
      :position-strategy="menuPositionStrategy"
      :side-offset="BOOTSTRAP_MENU_SIDE_OFFSET"
      class="flex flex-col"
      :class="[BOOTSTRAP_MENU_CLASSES, { [VIEWPORT_HEIGHT_CAP]: isHeightCapped }]"
    >
      <!-- the group scrolls rather than the menu, because reka-ui pins the menu's tabindex
           to -1 and a scrolling region must be focusable (axe scrollable-region-focusable);
           a focusable group, unlike a bare div, is an allowed child of a menu -->
      <DropdownMenuRadioGroup
        :model-value="filter.valueLabel"
        :tabindex="isHeightCapped ? 0 : undefined"
        :class="{ 'min-h-0 overflow-y-auto': isHeightCapped }"
        @update:model-value="selectOption(String($event))"
      >
        <template v-if="clearable">
          <DropdownMenuRadioItem value="" :class="[OPTION_CLASSES, { [OPTIONS_WITH_LOCKS_CLASSES]: hasLockedOptions }]">
            {{ filter.emptyText }}
          </DropdownMenuRadioItem>
          <DropdownMenuSeparator class="mx-0 my-[9px] bg-gray-300" />
        </template>
        <DropdownMenuRadioItem
          v-for="option in filter.options"
          :key="option.label"
          :value="option.label"
          :disabled="option.locked"
          :class="[OPTION_CLASSES, LOCKED_OPTION_CLASSES, { [OPTIONS_WITH_LOCKS_CLASSES]: hasLockedOptions }]"
        >
          <LockedOption v-if="option.locked" :label="option.label" hanging />
          <template v-else>{{ option.label }}</template>
        </DropdownMenuRadioItem>
      </DropdownMenuRadioGroup>
    </DropdownMenuContent>
  </DropdownMenu>
</template>
